import type { Token } from '@parser';
import { expectDefined } from '@utils';
import type { CadetParser } from './CadetParser';
import * as TT from './CadetTokens';
import { type Container } from './Container';
import { type ElementDirective } from './Directives';
import { Item } from './Item';
import type { Unparser } from './Unparser';

function sortElementDirectives(directives: ElementDirective[]): ElementDirective[] {
  const orderMap: Record<string, number> = {
    ':NOTE': 1,
    ':ASSISTANT': 2,
    ':BOUNDS': 3,
    ':PATH': 4
  };
  return directives.slice().sort((a, b) => {
    const orderA = orderMap[a.name] ?? 99;
    const orderB = orderMap[b.name] ?? 99;
    return orderA - orderB;
  });
}

export abstract class Statement extends Item {
  id?: string;
  title?: Token<string>;
  cls?: Token<string>;
  directives?: ElementDirective[];

  constructor(opts?: {
    start?: number;
    id?: string;
    title?: Token<string>;
    cls?: Token<string>;
    parent?: GroupStatement;
    directives?: ElementDirective[];
  }) {
    super(opts);
    this.id = opts?.id;
    this.title = opts?.title;
    this.cls = opts?.cls;
    this.directives = opts?.directives;
  }

  static parseStatement(p: CadetParser): Statement | undefined {
    if (p.hasArrowOnLine()) {
      const edge = EdgeStatement.parse(p);
      if (edge) return edge;
    }
    let statement: Statement | undefined = UnitStatement.parse(p, true);
    if (statement) {
      return statement;
    }
    statement = GroupStatement.parse(p);
    if (statement) {
      return statement;
    }
    return;
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      id: this.id,
      cls: this.cls?.toJSON(opts)
    };
  }

  protected unparseDirectives(u: Unparser): void {
    if (this.directives && this.directives.length > 0) {
      const sortedDirectives = sortElementDirectives(this.directives);
      // Use standard for loop to check if it's the last directive
      for (let i = 0; i < sortedDirectives.length; i++) {
        const directive = sortedDirectives[i];
        directive.unparse(u); // Write directive content
        // Add newline only if it's NOT the last directive in this block
        if (i < sortedDirectives.length - 1) {
          u.endLine();
        }
      }
      // DO NOT add endLine after the loop/last directive
    }
  }
}

export class UnitStatement extends Statement {
  constructor(
    id: string,
    opts?: {
      start?: number;
      title?: Token<string>;
      cls?: Token<string>;
      parent?: GroupStatement;
      directives?: ElementDirective[];
    }
  ) {
    super({ id, ...opts });
  }

  static parse(p: CadetParser, withBreak: boolean): UnitStatement | undefined {
    return p.withSavedPosition(commit => {
      const id = p.identifier();
      if (id === undefined) {
        return;
      }
      const cls = p.classIdentifier();
      const title = p.iString();
      if (withBreak && !p.break()) {
        return;
      }
      commit();
      return new UnitStatement(id.value, {
        start: id.start,
        title,
        cls,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    const baseJson = super.toJSON(opts);
    if (this.title) {
      (baseJson as Record<string, unknown>).title = this.title.toJSON(opts);
    }
    return baseJson;
  }

  unparse(u: Unparser): void {
    u.add(expectDefined(this.id));
    if (this.cls) {
      u.add(this.cls.text);
    }
    if (this.title) {
      u.add(this.title.text);
    }

    if (this.directives && this.directives.length > 0) {
      u.addLeft(';', ''); // Semicolon immediately after main statement text
      u.endLine(); // Newline after semicolon (for pretty mode separation)
      this.unparseDirectives(u); // Write directives (each gets endLine from unparseDirectives)
    }
    // If no directives, the terminating semicolon/newline is added by writeStatements
  }
}

export class EdgeStatement extends Statement {
  source: UnitStatement;
  target: UnitStatement;

  constructor(
    source: UnitStatement,
    target: UnitStatement,
    cls: Token<string>,
    opts?: {
      start?: number;
      id?: string;
      parent?: GroupStatement;
      directives?: ElementDirective[];
    }
  ) {
    super({ cls, ...opts });
    this.source = source;
    this.target = target;
  }

  static parse(p: CadetParser): EdgeStatement | undefined {
    return p.withSavedPosition(commit => {
      const edgeId = p.withSavedPosition(commitId => {
        const id = p.identifier();
        if (!id) return;
        if (!p.colon()) return;
        commitId();
        return id;
      });
      const source = UnitStatement.parse(p, false);
      if (!source) return;
      const cls = p.arrow();
      if (!cls) return;
      const target = UnitStatement.parse(p, false);
      if (!target) return;
      if (!p.break()) {
        return;
      } // Edge statement requires break
      commit();
      let start: number, id: string | undefined;
      if (edgeId) {
        start = edgeId.start;
        id = edgeId.value;
      } else {
        start = expectDefined(source.start);
      }
      return new EdgeStatement(source, target, cls, {
        start,
        id,
        parent: p.currentParent
      });
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      source: this.source.toJSON(opts),
      target: this.target.toJSON(opts)
    };
  }

  unparse(u: Unparser): void {
    if (this.id) {
      u.add(this.id);
      u.addLeft(':');
    }
    this.source.unparse(u);
    u.add(expectDefined(this.cls).value);
    this.target.unparse(u);

    if (this.directives && this.directives.length > 0) {
      u.addLeft(';', ''); // Semicolon immediately after main statement text
      u.endLine(); // Newline after semicolon (for pretty mode separation)
      this.unparseDirectives(u); // Write directives (each gets endLine from unparseDirectives)
    }
    // If no directives, the terminating semicolon/newline is added by writeStatements
  }
}

export class GroupStatement extends Statement implements Container {
  nestingLevel: number;
  items: Item[];

  constructor(
    items: Item[],
    opts?: {
      start?: number;
      id?: string;
      title?: Token<string>;
      cls?: Token<string>;
      parent?: GroupStatement;
      directives?: ElementDirective[];
    }
  ) {
    super(opts);
    this.items = items;
    this.nestingLevel = this.parent ? this.parent.nestingLevel + 1 : 0;
  }

  // Reverted to getter to fix TS error TS2610
  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get selfTerminating(): boolean {
    return true;
  }

  static parse(p: CadetParser): GroupStatement | undefined {
    return p.withSavedPosition(commit => {
      const groupId = p.withSavedPosition(commitId => {
        const id = p.identifier();
        if (!id) return;
        if (!p.colon()) return;
        commitId();
        return id;
      });
      const cls = p.classIdentifier();
      const title = p.iString();
      const brace = p.lBrace();
      if (!brace) return;
      let start: number, id: string | undefined;
      if (groupId) {
        start = groupId.start;
        id = groupId.value;
      } else {
        start = title?.start ?? cls?.start ?? brace.start;
      }
      const groupStatement = new GroupStatement([], {
        start,
        id,
        title,
        cls,
        parent: p.currentParent
      });
      p.consumeBreaks();
      p.pushContainmentContext({ parent: groupStatement });
      while (!p.isEOT && p.current.type !== TT.RBRACE) {
        const item = p.parseItem();
        if (!item) break;
        groupStatement.items.push(item);
        p.consumeBreaks();
      }
      const closingBrace = p.rBrace();
      if (!closingBrace) {
        p.error("Expected '}'", p.current.start);
        p.popContainmentContext();
        return;
      }
      p.break(); // Consume optional break after group
      commit();
      p.popContainmentContext();
      return groupStatement;
    });
  }

  toJSON(opts?: { compact?: boolean }): object {
    return {
      ...super.toJSON(opts),
      groupLevel: this.nestingLevel,
      items: this.items.map(item => item.toJSON(opts))
    };
  }

  unparse(u: Unparser): void {
    if (this.id) {
      u.add(this.id);
      u.addLeft(':');
    }
    if (this.cls) {
      u.add(this.cls.text);
    }
    if (this.title) {
      u.add(this.title.text);
    }

    u.start('{'); // Handles brace, spacing, indent, newline(pretty)

    // Write directives associated *with the group itself*
    this.unparseDirectives(u); // Adds newline after each directive
    // If the group has its own directives followed by items, insert a
    // separator between the last directive and the first item:
    // • pretty  → newline only
    // • compact → semicolon
    if (this.directives && this.directives.length > 0 && this.items.length > 0) {
      // Semicolon will appear in compact output; pretty output gets "".
      u.addLeft(';', '');
      // Ensure a newline before the first item in pretty output.
      u.endLine();
    }

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      item.unparse(u); // Handles item + its directives + internal semicolon/newlines if directives exist

      // Add semicolon separator if needed for non-terminating statements WITHOUT directives
      // AND only if it's NOT the last item in the group.
      const isLastItem = i === this.items.length - 1;
      if (
        !isLastItem &&
        item instanceof Statement &&
        !item.selfTerminating &&
        (!item.directives || item.directives.length === 0)
      ) {
        u.addLeft(';', '');
      }

      // Ensure separation between items in the group block (newline in pretty)
      // Don't add a final newline if it's the last item, as u.end() will handle it.
      if (!isLastItem) {
        u.endLine();
      }
    }

    // Add a newline before closing brace only if there were items (pretty mode)
    if (this.items.length > 0) {
      u.endLine();
    }

    u.end('}'); // Handles brace, outdent, newline(pretty)
  }
}
